import { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ChevronRight, Home, HelpCircle, ExternalLink } from "lucide-react";
import Seo from "@/components/Seo";
import Disclaimer from "@/components/Disclaimer";
import DeviceCard from "@/components/DeviceCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { fetchDevices } from "@/lib/api";
import { BRANDS } from "@/data/static";

export default function Brand() {
  const { slug } = useParams();
  const brand = BRANDS[slug];
  const [all, setAll] = useState([]);

  useEffect(() => {
    fetchDevices({ sort: "popularity" }).then(setAll).catch(() => {});
  }, []);

  const devices = useMemo(() => (brand ? all.filter(brand.pred) : []), [all, brand]);

  if (!brand) return <Navigate to="/fps-finder" replace />;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: brand.title,
      itemListElement: devices.map((d, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `https://gamingkits.online/device/${d.slug}`,
        name: d.name,
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: brand.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <>
      <Seo title={brand.metaTitle} description={brand.metaDesc} path={`/brand/${slug}`} jsonLd={jsonLd} />

      <div className="border-b border-border bg-graphite">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <nav className="mb-4 flex items-center gap-1.5 text-xs text-slate-500">
            <Link to="/" className="hover:text-white"><Home className="h-3.5 w-3.5" /></Link>
            <ChevronRight className="h-3 w-3" /><span className="text-slate-300">{brand.title}</span>
          </nav>
          <h1 className="font-display text-2xl font-black tracking-tight text-white sm:text-4xl">{brand.h1}</h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-300 sm:text-base">{brand.intro}</p>
          <span data-testid="brand-count" className="mt-3 inline-block rounded-full border border-neon/30 bg-neon/10 px-3 py-1 text-xs font-heading font-semibold text-neon">{devices.length} phones</span>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {devices.length === 0 ? (
          <div className="rounded-xl border border-border bg-slateSurface p-10 text-center text-slate-400">No devices from this brand yet.</div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {devices.map((d) => <DeviceCard key={d.slug} device={d} />)}
          </div>
        )}

        <div className="mt-8"><Disclaimer /></div>

        <section className="mt-10">
          <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-bold text-white sm:text-2xl"><HelpCircle className="h-5 w-5 text-cyber" /> Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="rounded-xl border border-border bg-slateSurface px-4">
            {brand.faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`brand-faq-${i}`} className="border-border">
                <AccordionTrigger className="text-left text-sm font-medium text-white hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-slate-300">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/fps-finder" className="rounded-lg bg-neon px-5 py-3 text-sm font-heading font-bold text-charcoal">Open FPS Finder</Link>
          <Link to="/compare" className="rounded-lg border border-cyber/40 bg-cyber/10 px-5 py-3 text-sm font-heading font-bold text-cyber">Compare Phones</Link>
          <a
            href={`https://phoneflip.pk/used-phones/${slug}`}
            target="_blank"
            rel="noopener noreferrer nofollow"
            data-testid="brand-cta-phoneflip"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-500/40 bg-slateSurface px-5 py-3 text-sm font-heading font-bold text-white hover:border-neon/50"
          >
            Buy {brand.name} on PhoneFlip <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </div>
    </>
  );
}
